import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

type DownloadStatus = 'PENDING' | 'SUCCESS' | 'ERROR' | 'MISSING_PARAMS';

export const useHandleDocumentDownload = () => {
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>('PENDING');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [downloadAttempted, setDownloadAttempted] = useState(false);

  const { userId, fileName, courseId } = useParams<{
    userId: string;
    fileName: string;
    courseId: string;
  }>();

  const getApiUrl = () => {
    if (window.location.href.includes('dev')) {
      return 'https://downloadcoursedocuments-374543744741.europe-west1.run.app';
    }
    return 'https://downloadcoursedocuments-397410004381.europe-west1.run.app';
  };

  const downloadDocument = async (userId: string, fileName: string, courseId: string): Promise<void> => {
    try {
      setDownloadStatus('PENDING');
      setErrorMessage('');
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId,
          fileName: fileName,
          courseId: courseId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Fehler beim Herunterladen! Status: ${response.status}`);
      }

      // Check if the response is JSON or a file
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        // Handle JSON response
        const data = await response.json();
        setDownloadStatus('SUCCESS');
        setErrorMessage(data.message || 'Dokument erfolgreich verarbeitet');
      } else {
        // Handle file download
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);

        setDownloadAttempted(true);
        setDownloadStatus('SUCCESS');
      }
    } catch (error) {
      console.error('Fehler:', error);
      setDownloadStatus('ERROR');
      setErrorMessage(error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten');
    }
  };

  const handleInitialDownload = async () => {
    // Check if all required parameters are present
    if (!userId || !fileName || !courseId) {
      setDownloadStatus('MISSING_PARAMS');
      setErrorMessage(
        'Die URL enthält nicht alle erforderlichen Parameter für den Download (userId, fileName, courseId).',
      );
      return;
    }

    // Attempt download
    await downloadDocument(userId, fileName, courseId);
  };

  const retryDownload = async () => {
    if (userId && fileName && courseId) {
      await downloadDocument(userId, fileName, courseId);
    } else {
      setDownloadStatus('ERROR');
      setErrorMessage('Keine Dokumentinformationen verfügbar.');
    }
  };

  useEffect(() => {
    // Short delay to ensure page is fully loaded
    const timer = setTimeout(() => {
      handleInitialDownload();
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  return {
    downloadStatus,
    errorMessage,
    downloadAttempted,
    retryDownload,
    canRetry: !!(userId && fileName && courseId),
  };
};
